import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Save, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { PlannerBulkDialog } from './planner/PlannerBulkDialog';
import { PlannerOrderGroup } from './planner/PlannerOrderGroup';
import { PlannerSelectionToolbar } from './planner/PlannerSelectionToolbar';
import { PLANNER_TYPES, plannerKey, plannerError } from './planner/plannerTypes';

const PlannerOrderAdmin = ({ apiClient }) => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [revision, setRevision] = useState(0);
  const [openAreas, setOpenAreas] = useState({});
  const [openSubs, setOpenSubs] = useState({});
  const [selected, setSelected] = useState({});
  const [request, setRequest] = useState(null);
  const [result, setResult] = useState(null);
  const [loadError, setLoadError] = useState('');
  const dragItem = useRef(null);
  const fetchOrder = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const { data } = await apiClient.get('/admin/planner-order');
      setAreas(data.areas || []); setRevision(data.revision || 0); setDirty(false); setSelected({});
      setOpenAreas(previous => Object.keys(previous).length ? previous : { [data.areas?.[0]?.area]: true });
    } catch (error) { setLoadError(plannerError(error)); }
    finally { setLoading(false); }
  }, [apiClient]);
  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const reorder = (aIdx, sIdx, from, to) => {
    setAreas(previous => {
      const copy = structuredClone(previous);
      const list = sIdx === null ? copy[aIdx].subtemas : copy[aIdx].subtemas[sIdx].items;
      if (to < 0 || to >= list.length) return previous;
      const [item] = list.splice(from, 1); list.splice(to, 0, item);
      return copy;
    });
    setDirty(true);
  };
  const selectGroup = (items, checked) => {
    const next = { ...selected };
    items.forEach(item => { if (checked) next[plannerKey(item)] = item; else delete next[plannerKey(item)]; });
    if (Object.keys(next).length > 200) { toast.error('Selecciona hasta 200 archivos por acción.'); return; }
    setSelected(next);
  };
  const startAction = (action, items = Object.values(selected), target = {}) => {
    if (dirty) { toast.error('Guarda primero el orden pendiente.'); return; }
    if (!items.length) return;
    setRequest({ action, items, ...target });
  };
  const onItemDrop = (aIdx, sIdx, dropIdx) => {
    const d = dragItem.current; dragItem.current = null;
    if (!d || saving || request) return;
    const item = areas[d.a].subtemas[d.s].items[d.i];
    if (d.a !== aIdx || d.s !== sIdx) {
      startAction('move', [item], { module: areas[aIdx].area, submodule: areas[aIdx].subtemas[sIdx].subtema });
    } else {
      const to = d.i < dropIdx ? dropIdx - 1 : dropIdx;
      if (to !== d.i) reorder(aIdx, sIdx, d.i, to);
    }
  };
  const handleSave = async () => {
    setSaving(true);
    const subtema_order = {}, item_order = {};
    areas.forEach(area => {
      subtema_order[area.area] = area.subtemas.map(sub => sub.subtema);
      area.subtemas.forEach(sub => { item_order[`${area.area}|${sub.subtema}`] = sub.items.map(item => item.id); });
    });
    try {
      const { data } = await apiClient.post('/admin/planner-order', { subtema_order, item_order, revision });
      setRevision(data.revision); setDirty(false); toast.success('Orden guardado.');
      await fetchOrder();
    } catch (error) { toast.error(plannerError(error)); }
    finally { setSaving(false); }
  };
  const onApplied = async data => {
    setRequest(null); setResult(data);
    if (data.failed) toast.error(`${data.succeeded} completados · ${data.failed} no se pudieron procesar.`);
    else toast.success(`${data.succeeded} archivo(s) actualizados.`);
    await fetchOrder();
  };
  if (loading) return <div className="py-12 text-center text-gray-500" data-testid="planner-order-loading">Cargando contenido…</div>;

  return <div data-testid="planner-order-admin-tab" className="pb-24 min-w-0">
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <h2 data-testid="planner-order-title" className="text-lg font-bold">Orden del Planificador</h2>
      <Button variant="outline" size="sm" data-testid="planner-order-refresh-btn" disabled={saving} onClick={fetchOrder}><RefreshCw size={14} className="mr-1.5" />Recargar</Button>
    </div>
    {loadError && <p role="alert" data-testid="planner-order-error" className="text-red-700 text-sm mb-4">{loadError}</p>}
    <div className="flex flex-wrap gap-3 text-xs mb-4">
      {Object.entries(PLANNER_TYPES).map(([key, cfg]) => <span data-testid={`planner-legend-${key}`} key={key} className="inline-flex items-center gap-1"><span className={`w-3 h-3 rounded-sm border ${cfg.cls}`} />{cfg.label}</span>)}
    </div>
    <PlannerSelectionToolbar count={Object.keys(selected).length} disabled={dirty || saving} onClear={() => setSelected({})} onAction={startAction} />
    {result && <div role="status" data-testid="planner-action-result" className={`text-sm mb-4 p-3 border rounded-lg ${result.failed ? 'border-red-300' : 'border-gray-200'}`}>
      {result.succeeded} archivo(s) actualizados{result.failed ? ` · ${result.failed} sin completar` : ''}.
      {result.items.filter(item => !item.success).map(item => <p key={plannerKey(item)} data-testid={`planner-action-failed-${item.id}`} className="break-words text-red-700">{item.title}: {item.error}</p>)}
    </div>}
    <div className="space-y-4">
      {areas.map((area, aIdx) => <section key={area.area} data-testid={`planner-area-${aIdx}`} className="min-w-0">
        <button data-testid={`planner-area-toggle-${aIdx}`} data-module={area.area} aria-expanded={!!openAreas[area.area]} onClick={() => setOpenAreas(previous => ({ ...previous, [area.area]: !previous[area.area] }))} className="w-full flex items-center justify-between gap-3 p-3 bg-black text-white rounded-lg text-left">
          <span className="font-bold uppercase text-sm break-words">{area.area}</span>
          {openAreas[area.area] ? <ChevronDown size={16} className="shrink-0" /> : <ChevronRight size={16} className="shrink-0" />}
        </button>
        {openAreas[area.area] && <div className="space-y-2 mt-2">
          {area.subtemas.map((sub, sIdx) => {
            const key = `${area.area}|${sub.subtema}`;
            return <PlannerOrderGroup key={key} area={area.area} sub={sub} aIdx={aIdx} sIdx={sIdx} subCount={area.subtemas.length} open={!!openSubs[key]} onToggle={() => setOpenSubs(previous => ({ ...previous, [key]: !previous[key] }))} selected={selected} onSelect={(item, checked) => selectGroup([item], checked)} onSelectGroup={selectGroup} moveSubtema={(a, from, dir) => reorder(a, null, from, from + dir)} moveItem={(a, s, from, dir) => reorder(a, s, from, from + dir)} dragItem={dragItem} onDrop={onItemDrop} disabled={saving} />;
          })}
        </div>}
      </section>)}
    </div>
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40">
      <div className="max-w-7xl mx-auto flex justify-end items-center gap-3">
        {dirty && <span data-testid="planner-order-dirty" className="text-xs text-amber-800">Cambios sin guardar</span>}
        <Button data-testid="planner-order-save-btn" disabled={saving || !!loadError} onClick={handleSave}>{saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}{saving ? 'Guardando…' : 'Guardar orden'}</Button>
      </div>
    </div>
    {request && <PlannerBulkDialog request={request} apiClient={apiClient} onClose={() => setRequest(null)} onApplied={onApplied} />}
  </div>;
};

export default PlannerOrderAdmin;